import React, { useEffect, useRef } from 'react';

interface Star {
  x: number;
  y: number;
  speed: number;
  size: number;
  trail: { x: number; y: number }[];
  maxTrailLength: number;
  angle: number;
}

interface SpaceObject {
  x: number;
  y: number;
  speed: number;
  size: number;
  type: 'star' | 'ufo' | 'invader';
  color: string;
  rotation: number;
  wobble: number;
}

export const RetroBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const angleRef = useRef<number>(Math.random() * Math.PI * 2);

  // Draw a star shape
  const drawStar = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rotation: number) => {
    ctx.save();
    ctx.translate(x, y);
    ctx.rotate(rotation);
    ctx.beginPath();
    for (let i = 0; i < 5; i++) {
      ctx.lineTo(Math.cos((18 + i * 72) * Math.PI / 180) * size,
                -Math.sin((18 + i * 72) * Math.PI / 180) * size);
      ctx.lineTo(Math.cos((54 + i * 72) * Math.PI / 180) * (size / 2),
                -Math.sin((54 + i * 72) * Math.PI / 180) * (size / 2));
    }
    ctx.closePath();
    ctx.restore();
  };

  // Draw a UFO
  const drawUFO = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.save();
    ctx.translate(x, y);
    // UFO body
    ctx.beginPath();
    ctx.ellipse(0, 0, size, size/2, 0, 0, Math.PI * 2);
    ctx.fill();
    // UFO top
    ctx.beginPath();
    ctx.arc(0, -size/4, size/2, 0, Math.PI, true);
    ctx.fill();
    // UFO lights
    ctx.fillStyle = '#00ff00';
    for (let i = -2; i <= 2; i++) {
      ctx.beginPath();
      ctx.arc(i * size/3, size/4, size/6, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  };

  // Draw a space invader
  const drawInvader = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
    ctx.save();
    ctx.translate(x, y);
    // Body
    ctx.beginPath();
    ctx.rect(-size/2, -size/2, size, size);
    ctx.fill();
    // Eyes
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(-size/4, -size/4, size/6, 0, Math.PI * 2);
    ctx.arc(size/4, -size/4, size/6, 0, Math.PI * 2);
    ctx.fill();
    // Antenna
    ctx.beginPath();
    ctx.moveTo(-size/4, -size/2);
    ctx.lineTo(-size/4, -size);
    ctx.moveTo(size/4, -size/2);
    ctx.lineTo(size/4, -size);
    ctx.stroke();
    ctx.restore();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const updateCanvasSize = () => {
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (rect) {
        canvas.width = rect.width;
        canvas.height = rect.height;
      }
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);

    // Create stars
    const stars: Star[] = [];
    const spaceObjects: SpaceObject[] = [];
    const numStars = 30;

    const createStar = (): Star => ({
      x: Math.random() * canvas.width * 0.2,
      y: Math.random() * canvas.height * 0.2,
      speed: Math.random() * 1 + 0.5,
      size: Math.random() * 1.5 + 0.5,
      trail: [],
      maxTrailLength: Math.floor(Math.random() * 3) + 2,
      angle: angleRef.current
    });

    // Initialize stars
    for (let i = 0; i < numStars; i++) {
      stars.push(createStar());
    }

    // Create space objects at intervals
    const createSpaceObject = (type: 'star' | 'ufo' | 'invader'): SpaceObject => ({
      x: -50,
      y: Math.random() * canvas.height,
      speed: type === 'ufo' ? 1 : type === 'star' ? 1.5 : 2,
      size: type === 'ufo' ? 15 : type === 'star' ? 12 : 10,
      type,
      color: type === 'star' ? '#ffff00' : 
             type === 'ufo' ? '#00ff00' : 
             `hsl(${Math.random() * 360}, 100%, 50%)`,
      rotation: 0,
      wobble: Math.random() * 0.05
    });

    // Set up intervals for different space objects
    const starInterval = setInterval(() => {
      if (spaceObjects.length < 30) {
        spaceObjects.push(createSpaceObject('star'));
      }
    }, 500);

    const ufoInterval = setInterval(() => {
      if (spaceObjects.length < 30) {
        spaceObjects.push(createSpaceObject('ufo'));
      }
    }, 800);

    const invaderInterval = setInterval(() => {
      if (spaceObjects.length < 30) {
        spaceObjects.push(createSpaceObject('invader'));
      }
    }, 400);

    // Change angle every 3 seconds
    const angleInterval = setInterval(() => {
      angleRef.current = Math.random() * Math.PI * 2;
      for (let i = 0; i < 3; i++) {
        stars.push(createStar());
      }
    }, 3000);

    // Animation loop
    function animate() {
      if (!ctx || !canvas) return;

      // Clear with more transparent black for smoother trail effect
      ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw stars
      for (let i = stars.length - 1; i >= 0; i--) {
        const star = stars[i];
        
        star.x += Math.cos(star.angle) * star.speed;
        star.y += Math.sin(star.angle) * star.speed;

        star.trail.push({ x: star.x, y: star.y });
        if (star.trail.length > star.maxTrailLength) {
          star.trail.shift();
        }

        ctx.beginPath();
        ctx.moveTo(star.trail[0].x, star.trail[0].y);
        for (let j = 1; j < star.trail.length; j++) {
          ctx.lineTo(star.trail[j].x, star.trail[j].y);
        }
        ctx.strokeStyle = `rgba(255, 255, 255, ${0.3 + Math.random() * 0.2})`;
        ctx.lineWidth = star.size * 0.8;
        ctx.stroke();

        ctx.fillStyle = '#fff';
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fill();

        if (star.x < -100 || star.x > canvas.width + 100 || 
            star.y < -100 || star.y > canvas.height + 100) {
          stars.splice(i, 1);
        }
      }

      // Update and draw space objects
      for (let i = spaceObjects.length - 1; i >= 0; i--) {
        const obj = spaceObjects[i];
        
        obj.x += obj.speed;
        obj.y += Math.sin(obj.x * obj.wobble) * 2;
        obj.rotation += 0.02;

        ctx.fillStyle = obj.color;
        ctx.strokeStyle = obj.color;

        switch (obj.type) {
          case 'star':
            drawStar(ctx, obj.x, obj.y, obj.size, obj.rotation);
            ctx.fill();
            break;
          case 'ufo':
            drawUFO(ctx, obj.x, obj.y, obj.size);
            break;
          case 'invader':
            drawInvader(ctx, obj.x, obj.y, obj.size);
            break;
        }

        if (obj.x > canvas.width + 100) {
          spaceObjects.splice(i, 1);
        }
      }

      requestAnimationFrame(animate);
    }

    animate();

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
      clearInterval(angleInterval);
      clearInterval(starInterval);
      clearInterval(ufoInterval);
      clearInterval(invaderInterval);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 0,
        background: 'linear-gradient(to bottom, #000000, #1a0033)'
      }}
    />
  );
}; 